import { useOrg } from '@/organization/useOrg';
import { Box, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  SubprojectBuildingDetails,
  TechnologyParameter,
  useSubprojectStore,
} from '../../stores/SubprojectStore.ts';
import { DecarbPageLayout } from '../components/DecarbPageLayout.tsx';
import { DecarbTabsPanel } from '../components/DecarbTabsPanel.tsx';
import { HelperText } from '../components/HelperText.tsx';
import { usePencilsDown } from '../hooks/usePencilsDown.ts';
import { useHVACQuery } from '../queries/hvac';
import { safelySaveSubproject } from '../saveHelpers.ts';
import { HVAC, Tab, TabKinds } from '../types';
import {
  createEmptyBuildingAlternative,
  createEmptySubprojectTechnology,
  storedCategoryCapitalCost,
  updateTechParams,
} from '../utils.ts';
import { HVACFooterBar } from './HVACFooterBar.tsx';
import { HVACForBuildingDetails } from './HVACForBuildingDetails.tsx';
import {
  computeHVACMinMax,
  getEnergyUsageForHvac,
  isHvacAllowedOnTab,
} from './hvacUtils';

/**
 * Fill in the alternatives and hvac tech params a server payload may be missing,
 * on a clone. Applied to everything the server hands back — the load fetch and
 * the save result alike — because the editing handlers below bail out when
 * `technologyParams['hvac'].params` is absent.
 */
const withAlternativeDefaults = (
  subproject: SubprojectBuildingDetails,
): SubprojectBuildingDetails => {
  const updated = structuredClone(subproject);
  for (const alternativeTab of TabKinds) {
    if (!updated[alternativeTab]) {
      updated[alternativeTab] = createEmptyBuildingAlternative();
    }
    // stored alternatives have shipped without this field before
    updated[alternativeTab].technologyParams ??= {};
    if (!updated[alternativeTab].technologyParams['hvac']) {
      updated[alternativeTab].technologyParams['hvac'] =
        createEmptySubprojectTechnology('hvac');
    }
  }
  return updated;
};

export const HVACSelection = () => {
  const pencilsDown = usePencilsDown();
  const queryClient = useQueryClient();
  const subprojectStore = useSubprojectStore();
  const { data: hvacs = [] } = useHVACQuery();
  const hvacMap = useMemo(() => {
    const map: Record<string, HVAC> = {};
    for (const h of hvacs) map[h.id] = h;
    return map;
  }, [hvacs]);
  const minMax = useMemo(() => computeHVACMinMax(hvacs), [hvacs]);
  const { subId } = useParams();
  const { selectedOrg } = useOrg();
  const [subproject, setSubproject] = useState<SubprojectBuildingDetails>();
  const [tab, setTab] = useState<Tab>('conventionalAlternative');
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  // The exact object last handed to us by the server (on load, or as the result
  // of a save). Identity comparison against `subproject` answers two questions
  // at once: whether there is anything to save, and whether the stored,
  // backend-computed cost figures still describe what is on screen.
  const [syncedSubproject, setSyncedSubproject] =
    useState<SubprojectBuildingDetails>();
  const costsArePending = subproject !== syncedSubproject;
  // Incremented as each save request is *issued*, so a response can tell
  // whether it is still the newest one outstanding.
  const saveSeqRef = useRef(0);

  /**
   * Adopt what the backend handed back from a save. Costs such as the passive
   * 15%-of-design-cost charge only exist after the server has run, so without
   * this the figures on screen stay frozen at their page-load values for the
   * whole session.
   *
   * Two independent guards, because two different things can go wrong:
   *
   * - The *sequence* guard covers out-of-order completion. Saves can overlap,
   *   and if an earlier request resolves last its stale result would drag
   *   `syncedSubproject` backwards while `subproject` holds the newer one —
   *   `costsArePending` would spuriously flip to true, blanking the cost tile
   *   and the tab badges and provoking a redundant round trip.
   * - The *snapshot* guard covers an edit made while a save is in flight. That
   *   case does not bump the sequence (the replacement request has not been
   *   issued yet — its debounce timer is still pending), so only comparing
   *   identities can catch it. The newer state stands and already has its own
   *   save queued; overwriting it here would drop the edit. `syncedSubproject`
   *   still moves: it means "what the server last confirmed", and with a newer
   *   unsaved edit on screen `costsArePending` is then correctly true.
   */
  const adoptSaveResult = useCallback(
    (
      savedSnapshot: SubprojectBuildingDetails,
      result: SubprojectBuildingDetails,
      seq: number,
    ) => {
      if (seq !== saveSeqRef.current) return;
      const refreshed = withAlternativeDefaults(result);
      setSyncedSubproject(refreshed);
      setSubproject((current) =>
        current === savedSnapshot ? refreshed : current,
      );
    },
    [],
  );

  // fetch subproject saved in database. If the conventional, reducedCarbon or zeroCarbon alternatives are null,
  // populate them with dummy data (just for development).
  useEffect(() => {
    if (!subId || !selectedOrg) {
      return;
    }

    subprojectStore
      .fetchSubprojectDetails(subId, selectedOrg.id)
      .then((subproject) => {
        // Defaults are applied on a clone before it becomes state — mutating
        // state in place later skips re-renders
        const updatedSubproject = withAlternativeDefaults(subproject);
        setSubproject(updatedSubproject);
        setSyncedSubproject(updatedSubproject);
        return updatedSubproject;
      })
      .catch(console.error);
  }, [subId, subprojectStore, selectedOrg]);

  // Debounced auto-save after selection changes
  useEffect(() => {
    if (pencilsDown) return;
    if (!subproject || !subId || !selectedOrg) return;

    // Nothing has changed since the server last spoke — that covers the state
    // set by the initial load as well as the state we adopt from a save result,
    // neither of which should trigger a save (the latter would loop).
    if (subproject === syncedSubproject) return;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    const savedSnapshot = subproject;
    saveTimeoutRef.current = setTimeout(() => {
      setIsSaving(true);
      // Claimed here rather than where the effect schedules the timeout: the
      // sequence must track requests actually in flight.
      const seq = ++saveSeqRef.current;
      safelySaveSubproject(subId, selectedOrg.id, savedSnapshot, queryClient)
        .then((result) => adoptSaveResult(savedSnapshot, result, seq))
        .catch(console.error)
        .finally(() => setIsSaving(false));
    }, 500);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [
    pencilsDown,
    subproject,
    syncedSubproject,
    subId,
    selectedOrg,
    queryClient,
    adoptSaveResult,
  ]);

  // handle increment, decrement and change (from user input in TextField) of
  // selected HVAC system counts
  const handleChangeHvacCount = useCallback(
    (hvacId: string, newCount: number) => {
      setSubproject((previousSubproject) => {
        if (!previousSubproject) {
          return;
        }

        if (!previousSubproject?.[tab]?.technologyParams?.['hvac']?.params) {
          return;
        }

        return updateTechParams(previousSubproject, tab, 'hvac', (params) => {
          if (newCount > 0) {
            return params.map((hvacSystem) =>
              hvacSystem.techId === hvacId
                ? { ...hvacSystem, units: newCount }
                : hvacSystem,
            );
          } else {
            return params.filter(
              (hvacSystem: TechnologyParameter) => hvacSystem.techId !== hvacId,
            );
          }
        });
      });
    },
    [tab],
  );

  // add a HVACSystem to list of selected HVAC systems
  const handleAddHvac = useCallback(
    (hvacId: string, limit: number) => {
      setSubproject((previousSubproject) => {
        if (!previousSubproject) {
          return;
        }

        if (!previousSubproject?.[tab]?.technologyParams?.['hvac']?.params) {
          return;
        }

        return updateTechParams(previousSubproject, tab, 'hvac', (params) => [
          ...params,
          { techId: hvacId, units: limit },
        ]);
      });
    },
    [tab],
  );

  // copy selected HVAC systems over from the previous alternative
  const handleCopyHvacSettings = useCallback(
    (tab: Tab) => {
      setSubproject((previousSubproject) => {
        if (!previousSubproject) {
          return;
        }

        // Don't copy if already on conventional
        if (tab === 'conventionalAlternative') {
          return;
        }

        // Determine which alternative to copy FROM
        let tabToCopyFrom: Tab = 'conventionalAlternative';
        if (tab === 'zeroCarbonAlternative') {
          tabToCopyFrom = 'reducedCarbonAlternative';
        }

        const HVACList: TechnologyParameter[] =
          previousSubproject[tabToCopyFrom].technologyParams['hvac']?.params ||
          [];

        if (HVACList.length === 0) {
          return;
        }

        // Filter HVAC systems based on target alternative
        const filteredHVACList = HVACList.filter((hvacParam) => {
          const hvac = hvacMap[hvacParam.techId];
          if (!hvac) {
            console.error(
              'Unable to locate HVAC unit during copy, not found in storage',
            );
            return false;
          }

          // Same tab-eligibility rules the available list applies
          return isHvacAllowedOnTab(hvac, tab, minMax);
        });

        const excludedCount = HVACList.length - filteredHVACList.length;
        if (excludedCount > 0) {
          console.log(
            `[Copy] ${excludedCount} item(s) excluded during copy to ${tab} for hvac`,
          );
        }

        return updateTechParams(
          previousSubproject,
          tab,
          'hvac',
          () => filteredHVACList,
        );
      });
    },
    [hvacMap, minMax],
  );

  const getCostAndEnergyMetrics = useCallback(
    (tab: Tab): { energyDemand: number; cost?: number } | undefined => {
      const alternative = subproject?.[tab];
      const params = alternative?.technologyParams['hvac']?.params;
      if (!params) {
        return undefined;
      }
      const energyDemand = params.reduce((sum, b) => {
        const hvac = hvacMap[b.techId];
        if (!hvac) {
          console.error('Unable to locate HVAC unit, not found in storage');
          return sum;
        }
        return (
          sum +
          getEnergyUsageForHvac(
            hvac,
            subproject?.airExchangeFactor ?? 0,
            b.units,
          )
        );
      }, 0);

      // The badge prices EVERY selection -- active and passive -- from the
      // category total the backend computed and stored, exactly like the
      // CAPITAL COST tile (HVACForBuildingDetails). The stored figure carries
      // what a local `capitalCostPerUnit x units` cannot: the regional
      // multiplier, a per-row inflation ratio (each catalog row's own cost
      // year), and the passive 15%-of-design-cost charge. Because the inflation
      // ratio differs row by row, local arithmetic would be wrong by a
      // different factor on each alternative and corrupt the
      // alternative-vs-conventional percentage, not just the dollar figure.
      // So there is no second copy of the cost chain here; `costsArePending`
      // says the stored figure predates what is on screen, and then there is
      // no figure to show yet.
      const cost = storedCategoryCapitalCost(
        alternative,
        'hvac',
        costsArePending,
      );

      // Nothing worth a badge: no cost figure (or a zero one) and no demand.
      if (!cost && energyDemand === 0) {
        return undefined;
      }
      return { energyDemand, cost };
    },
    [subproject, hvacMap, costsArePending],
  );

  const conventionalMetrics = useMemo(
    () => getCostAndEnergyMetrics('conventionalAlternative'),
    [getCostAndEnergyMetrics],
  );
  const reducedCarbonMetrics = useMemo(
    () => getCostAndEnergyMetrics('reducedCarbonAlternative'),
    [getCostAndEnergyMetrics],
  );
  const zeroCarbonMetrics = useMemo(
    () => getCostAndEnergyMetrics('zeroCarbonAlternative'),
    [getCostAndEnergyMetrics],
  );

  if (!selectedOrg) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant={'h5'}>
          Please select an organization from the user menu to access this
          project.
        </Typography>
      </Box>
    );
  }

  return (
    <DecarbPageLayout
      title={'select HVAC Components'}
      tabs={
        <DecarbTabsPanel
          currentTab={tab}
          onClick={setTab}
          conventionalMetrics={conventionalMetrics}
          reducedCarbonMetrics={reducedCarbonMetrics}
          zeroCarbonMetrics={zeroCarbonMetrics}
        />
      }
      footer={
        <HVACFooterBar
          currentTab={tab}
          changeTab={setTab}
          saveSubproject={async () => {
            if (pencilsDown) return;
            if (subproject && subId && selectedOrg) {
              try {
                const seq = ++saveSeqRef.current;
                const result = await safelySaveSubproject(
                  subId,
                  selectedOrg.id,
                  subproject,
                  queryClient,
                );
                // Same adoption as the debounced save, guards included — the
                // two paths can be in flight at once.
                adoptSaveResult(subproject, result, seq);
              } catch (error) {
                console.error('Failed to save HVAC data:', error);
                alert('Failed to save changes. Please try again.');
                throw error;
              }
            }
          }}
        />
      }
    >
      {subproject && (
        <HVACForBuildingDetails
          onAddHvac={handleAddHvac}
          tab={tab}
          onChangeHvacCount={handleChangeHvacCount}
          onCopyConvetionalAlternative={handleCopyHvacSettings}
          subproject={subproject}
          costsArePending={costsArePending}
          isLoading={isSaving}
          readOnly={pencilsDown}
          emptyText={
            pencilsDown ? undefined : (
              <HelperText
                tab={tab}
                onCopySelection={() => handleCopyHvacSettings(tab)}
              />
            )
          }
        />
      )}
    </DecarbPageLayout>
  );
};
